import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '@/lib/googleMaps';

// Phase 8: in-app map view for the venue picker (Venues.jsx) — one marker
// per geo-tagged venue, click to select. Venues without coordinates simply
// don't get a pin (same "silent, not an error" treatment as the list
// view's distance sort — see academies.py's nearby endpoint).
export function VenueMap({ venues, selectedVenueId, onSelect, center }) {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapElRef.current) return;
        mapRef.current = new google.maps.Map(mapElRef.current, {
          center: center || { lat: 12.9716, lng: 77.5946 }, // Bangalore fallback
          zoom: center ? 12 : 5,
          disableDefaultUI: true,
          zoomControl: true,
        });
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status !== 'ready' || !window.google) return;
    const google = window.google;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const geoTagged = venues.filter((v) => v.latitude != null && v.longitude != null);
    const bounds = new google.maps.LatLngBounds();

    geoTagged.forEach((v) => {
      const marker = new google.maps.Marker({
        position: { lat: v.latitude, lng: v.longitude },
        map: mapRef.current,
        title: v.name,
        icon: v.id === selectedVenueId ? {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#CCFF00',
          fillOpacity: 1,
          strokeColor: '#000',
          strokeWeight: 2,
        } : undefined,
      });
      marker.addListener('click', () => onSelect(v.id));
      markersRef.current.push(marker);
      bounds.extend(marker.getPosition());
    });

    if (geoTagged.length > 1) {
      mapRef.current.fitBounds(bounds, 60);
    } else if (geoTagged.length === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(14);
    }
  }, [status, venues, selectedVenueId, onSelect]);

  if (status === 'error') {
    return (
      <div className="h-full flex items-center justify-center text-sm text-white/40 text-center px-6">
        Map couldn't load — check your connection and try again.
      </div>
    );
  }

  return <div ref={mapElRef} className="w-full h-full" data-testid="venue-map" />;
}

export default VenueMap;
